'use client';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { getLocalizedPath } from '@/lib/getLocalizedPath';
import { Instagram, LinkedIn, TikTok } from './icons';
import { useLang } from './ui';

export default function SiteFooter() {
  const t = useTranslations('v9.footer');
  const { locale } = useLang();
  const L = (p: string) => getLocalizedPath(locale, p);
  const links: [string, string][] = [
    [L('/reserve/riyadh'), t('riyadh')],
    [L('/add-ons'), t('addons')],
    [L('/about'), t('about')],
    [L('/help'), t('help')],
    [L('/pages/privacy-policy'), t('privacy')],
    [L('/pages/terms-and-conditions'), t('terms')],
    [L('/contact-us'), t('contact')],
    [L('/join-as-vendor'), t('partner')],
    [L('/blogs'), t('blog')],
  ];
  const social: [string, string, typeof Instagram][] = [
    ['https://www.instagram.com/mila.celebrations', 'Instagram', Instagram],
    ['https://www.tiktok.com/@mila.celebrations', 'TikTok', TikTok],
    ['https://www.linkedin.com/company/mila-celebration', 'LinkedIn', LinkedIn],
  ];
  const sep = "[&>span+span]:before:mx-[9px] [&>span+span]:before:opacity-50 [&>span+span]:before:content-['·'] max-lg:[&>span+span]:before:content-none";
  return (
    <footer className="relative z-[3] bg-night-2 py-5 pb-4 text-[13.5px] text-white/[.72] before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-[linear-gradient(90deg,rgba(217,180,95,0),rgba(217,180,95,.8)_28%,rgba(217,180,95,.8)_72%,rgba(217,180,95,0))]">
      <div className="v9-wrap">
        <div className="flex items-center gap-[clamp(16px,3vw,40px)] max-lg:flex-col max-lg:gap-2.5">
          <Link href={L('/')} aria-label="Mila" className="flex-none">
            <img src="/v9/logo-white.webp" alt="Mila" width={900} height={381} loading="lazy" className="h-[30px] w-auto" />
          </Link>
          <nav aria-label="Footer" className="flex flex-1 flex-wrap justify-center gap-x-[clamp(14px,2vw,28px)] gap-y-0.5 text-[14px] font-bold max-lg:gap-x-[18px] max-lg:text-[13.5px]">
            {links.map(([href, label]) => (
              <Link key={href} href={href} className="inline-flex min-h-9 items-center border-b border-transparent text-white/[.88] transition-colors hover:border-gold/60 hover:text-sun">{label}</Link>
            ))}
          </nav>
          <div className="flex flex-none gap-2">
            {social.map(([href, label, Icon]) => (
              <a key={label} href={href} target="_blank" rel="noopener" aria-label={label} className="grid h-9 w-9 place-items-center rounded-full text-white shadow-[inset_0_0_0_1px_rgba(217,180,95,.55)] transition-colors hover:bg-sun hover:text-deep hover:shadow-none">
                <Icon className="h-[18px] w-[18px]" />
              </a>
            ))}
          </div>
        </div>
        <div className="mt-3.5 flex flex-wrap justify-between gap-x-6 gap-y-1 border-t border-white/10 pt-3 font-body text-[12.5px] leading-[1.7] text-white/[.58] max-lg:flex-col max-lg:items-center max-lg:text-center">
          <p className={`flex flex-wrap items-center max-lg:flex-col max-lg:gap-0.5 ${sep}`}>
            <span>{t('company')}</span>
            <span>{t('city')}</span>
            <span><a href="mailto:info@mila.day" dir="ltr" className="text-white/[.85] hover:text-sun">info@mila.day</a></span>
          </p>
          <p className={`flex flex-wrap items-center max-lg:flex-col max-lg:gap-0.5 ${sep}`}>
            <span>{t('rights', { year: new Date().getFullYear() })}</span>
            <span>{t('cr')}</span>
            <span>{t('vat')}</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
